import { useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectSidebarCollapsed } from '@store/uiSlice';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import ImpersonationBanner from './ImpersonationBanner';
import CommandPalette from '@components/ui/CommandPalette';
import TimerWidget from '@components/timeTracking/TimerWidget';
import CookieConsentBanner from '@components/ui/CookieConsentBanner';
import AgentChatFAB from '@components/zunoStudio/AgentChatFAB';
import CopilotProvider from '@components/copilot/CopilotProvider';
import CopilotCommandPalette from '@components/copilot/CopilotCommandPalette';
import { useCopilot } from '@components/copilot/CopilotProvider';

/** Renders the palette only when the bound copilot allows that surface. */
function CopilotPaletteGate() {
  const { paletteEnabled, boundAgent } = useCopilot();
  // With no copilot bound the palette stays available as a general shortcut —
  // it is a navigation aid, not copilot-only.
  if (boundAgent && !paletteEnabled) return null;
  return <CopilotCommandPalette />;
}
import { CopilotWarningBar } from '@components/copilot/CopilotWarnings';
import WorkspaceSetupModal from '@components/workspace/WorkspaceSetupModal';

/**
 * MainLayout — The primary authenticated layout
 *
 * Structure:
 * ┌─────────────────────────────────────────┐
 * │  Sidebar  │  Topbar                     │
 * │           │─────────────────────────────│
 * │           │                             │
 * │           │  Content (Outlet)           │
 * │           │                             │
 * │           │                             │
 * └─────────────────────────────────────────┘
 */
const MainLayout = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const [showCreateWs, setShowCreateWs] = useState(false);

  return (
    <CopilotProvider>
    <div className="flex flex-col min-h-screen bg-surface-50">
      {/* SA3 — impersonation banner shows only when SA is logged-in-as-user */}
      <ImpersonationBanner />

      {/* Copilot Command Palette (global '/' shortcut) */}
      {/* Gated on the resolved copilot's surface list — it was unconditional,
          so turning "Command palette" off in the builder changed nothing. */}
      <CopilotPaletteGate />

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <Sidebar onCreateWorkspace={() => setShowCreateWs(true)} />

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Topbar */}
          <Topbar />

          {/* Copilot Proactive Warnings */}
          <CopilotWarningBar />

          {/* Page content — all child routes render here */}
          <main className="flex-1 p-4 sm:p-6 lg:p-8">
            <div className="w-full">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* Command Palette (Cmd+K) */}
      <CommandPalette
        onAction={(action) => {
          if (action === 'create-task') {
            window.dispatchEvent(new CustomEvent('zentrix:create-task'));
          }
        }}
      />

      {/* Floating Timer Widget (persists across pages) */}
      <TimerWidget />

      {/* Zuno Studio — Agent Chat FAB */}
      <AgentChatFAB />

      {/* GDPR/DPDP Cookie Consent Banner */}
      <CookieConsentBanner />

      {/* One-Click Workspace Setup Modal */}
      <WorkspaceSetupModal
        open={showCreateWs}
        onClose={() => setShowCreateWs(false)}
      />
    </div>
    </CopilotProvider>
  );
};

export default MainLayout;
